import { Injectable } from '@angular/core';
import { LtoPricingRequest, LtoPricingResponse } from '@core/models/lto-pricing.model';
import { AuthService } from '@core/services/index';
import { environment } from '@env';
import { LoggingService, LogSeverity } from '@core/services/logging.service';
import { io, Socket } from 'socket.io-client';
import { CustomerPlaybackService } from '@shared/services/customer-playback.service';
import { DeviceTypeService } from './device-type.service';

declare global {
  interface Window {
    __IO_SOCKET__?: Socket;
  }
}

interface ServerToClientEvents {
  connect: () => void;
  authenticationError: () => void;
  audioProcessed: (response: AudioProcessedEvent) => void;
  agentAudioReceived: (audio: AgentAudioReveivedEvent) => void;
  agentResponseReceived: (response: AgentResponseReceivedEvent) => void;
  agentIsProcessing: (response: AgentIsProcessingEvent) => void;
  transcriptReceived: (transcript: string, sessionId: string) => void;
  uniqueAudioReceived: (transcript: string, sessionId: string, phraseId: string) => void;
  customerAudioReceived: (audio: ArrayBuffer) => void;
  sessionStarted: (state: SessionStartedState) => void;
  sessionResumed: () => void;
  cartPriced: (cartPricedEvent: CartPricedEvent) => void;
  recommendationsPriced: (recommendationsPricedEvent: RecommendationsPricedEvent) => void;
  start: (sessionEvent: SessionEvent) => void;
  stop: (sessionId: string) => void;
  closeLane: (sessionId: string) => void;
  openLane: (sessionId: string) => void;
  standbyLane: (sessionId: string) => void;
  agentSwitchedLanguage: (response: LanguageChangeEvent) => void;
  pricingResponse: (response: LtoPricingResponse) => void;
}

interface ClientToServerEvents {
  endSession: (sessionId: string) => void;
  audioReceived: (request: AudioReceivedEvent) => void;
  startSession: (sessionConfiguration: SessionConfiguration) => void;
  startOrderConversation: (sessionConfiguration: SessionConfiguration) => void;
  resumeSession: (sessionId: string) => void;
  resumeSessionFromUniqueAudio: (sessionId: string, phraseId: string) => void;
  sendCustomAudio: (sessionId: string, audioId: string) => void;
  pricingRequest: (request: LtoPricingRequest) => void;
  subscribeCustomerPlayback: () => void;
  unsubscribeCustomerPlayback: () => void;
}

type SessionStartedState = {
  sessionId: string;
  isRecording: boolean;
};

type AudioReceivedEvent = {
  audio?: Blob;
  sessionId: string;
  laneId: string;
};

export type DaypartOverride = 'breakfast' | 'rest-of-day';

type SessionConfiguration = {
  sessionId: string;
  sessionType: SessionType;
  daypartOverride?: DaypartOverride;
};

type AudioProcessedEvent = {
  sessionId: string;
  audio: Blob;
  text: string;
  sessionResults: SessionResults;
};

type AgentAudioReveivedEvent = {
  sessionId: string;
  audio: Blob;
};

type AgentResponseReceivedEvent = {
  sessionId: string;
  text: string;
  sessionResults: SessionResults;
};

export type AgentIsProcessingEvent = {
  sessionId: string;
  action: string;
};

type SessionResults = {
  parameters: object;
  customTrigger: string;
  page: string;
  intent: string;
};

type CartPricedEvent = {
  sessionId: string;
  cart: object;
};

type RecommendationsPricedEvent = {
  sessionId: string;
  recommendations: object;
};

export type LanguageChangeEvent = {
  sessionId: string;
  language: string;
};

export enum SessionType {
  BROWSER = 'BROWSER',
  DRIVETHRU = 'DRIVETHRU',
}

export type SessionEvent = {
  storeId: string;
  laneId: string;
  sessionId: string;
  sessionType: SessionType;
};

@Injectable({ providedIn: 'root' })
export class IoService {
  public socketio: Socket<ServerToClientEvents, ClientToServerEvents> | undefined;

  private readonly maxConnectRetries = 5;
  private connectRetries = 0;

  // Queue of tasks to run once the socket is connected/reconnected
  private pendingOnConnect: Array<() => void> = [];

  // Desired customerPlayback subscription state. Re-asserted on every (re)connect because a reconnect
  // yields a new socket id and the webproxy drops the prior subscription on disconnect.
  private customerPlaybackSubscribed = false;

  constructor(
    private logger: LoggingService,
    private authService: AuthService,
    private customerPlaybackService: CustomerPlaybackService,
    private deviceTypeService: DeviceTypeService,
  ) {}

  /** Public: Idempotent. Safe to call repeatedly. */
  initializeSocket(): void {
    this.logger.writeLog('[IoService] Initializing socket connection to proxy...', {
      severity: LogSeverity.DEBUG,
    });

    if (typeof window !== 'undefined' && window.__IO_SOCKET__) {
      this.socketio = window.__IO_SOCKET__;
      this.logger.writeLog('[IoService] Reusing existing socket (HMR).', { severity: LogSeverity.DEBUG });
      return;
    }

    if (this.socketio) {
      // Corrected message (it *is* present)
      this.logger.writeLog('[IoService] Socket instance already present; skipping initialize.', {
        severity: LogSeverity.DEBUG,
      });
      return;
    }

    const { proxyUrl, defaultCorrelationId } = environment;

    const storeId = this.safeGetLocal('storeId') ?? undefined;
    const correlationId = this.safeGetLocal('correlationId') ?? defaultCorrelationId;

    if (storeId) {
      this.logger.setStoreId(storeId);
    }

    this.socketio = io(proxyUrl || '', {
      withCredentials: true,
      transports: ['websocket'],
      // Use function-style auth so the token is fetched right before the connection attempt
      auth: (cb: (auth: { token?: string }) => void) => {
        this.getAccessTokenSafely()
          .then((token) => cb({ token }))
          .catch((err) => {
            this.logger.writeLog('[IoService] Failed to get access token for socket auth.', {
              severity: LogSeverity.WARNING,
              details: err,
            });
            cb({}); // send empty auth to preserve legacy behavior
          });
      },
      query: {
        storeId,
        sessionType: SessionType.BROWSER,
        correlationId,
        deviceType: this.deviceTypeService.getDeviceType(),
        'ngsw-bypass': 'true',
      },
    });

    if (typeof window !== 'undefined') {
      window.__IO_SOCKET__ = this.socketio;
    }

    // --- Core connection lifecycle handlers (re-attached on every fresh initialize) ---
    this.socketio.on('connect', () => {
      this.logger.setSocketId(this.socketio?.id ?? '');
      this.logger.writeLog('[IoService] Webapp connected to Webproxy.', {
        severity: LogSeverity.INFO,
      });
      this.connectRetries = 0;
      this.flushOnConnectQueue();
    });

    // Using the Manager for reconnect events, as in legacy code
    this.socketio.io.on('reconnect_attempt', (attempt) => {
      this.logger.writeLog(`[IoService] Reconnecting to Webproxy (attempt ${attempt})`, {
        severity: LogSeverity.INFO,
      });
    });

    this.socketio.io.on('reconnect', () => {
      this.logger.writeLog('[IoService] Reconnected to Webproxy.', {
        severity: LogSeverity.INFO,
      });
      this.connectRetries = 0;
      this.flushOnConnectQueue();
      // The reconnect carries a new socket id and the webproxy dropped the old subscription on
      // disconnect, so re-assert it here.
      if (this.customerPlaybackSubscribed) {
        this.socketio?.emit('subscribeCustomerPlayback');
      }
    });
  }

  // --------------------
  // Legacy helper queue
  // --------------------
  private runWhenConnected(task: () => void): void {
    //this.logger.writeLog('[IoService] Queuing up task', { severity: LogSeverity.DEBUG })
    if (this.socketio?.connected) {
      //this.logger.writeLog('[IoService] Running queued task immediately', { severity: LogSeverity.DEBUG })
      task();
    } else {
      this.pendingOnConnect.push(task);
    }
  }

  private flushOnConnectQueue(): void {
    const tasks = this.pendingOnConnect.splice(0);
    for (const t of tasks) {
      try {
        t();
      } catch (e) {
        this.logger.writeLog('[IoService] Error running queued task', {
          severity: LogSeverity.ERROR,
          details: e,
        });
      }
    }
  }

  /** Keep public behavior: removes **all** listeners on current socket. */
  removeListeners(): void {
    this.socketio?.removeAllListeners();
  }

  // --------------------
  // Safe emit wrapper
  // --------------------
  private ensureInitialized(): void {
    if (!this.socketio) {
      this.logger.writeLog('[IoService] No socket instance; initializing now.', {
        severity: LogSeverity.WARNING,
      });
      this.initializeSocket();
    }
  }

  /** Centralized emit that respects the connection queue. */
  private emit<K extends keyof ClientToServerEvents>(ev: K, ...params: Parameters<ClientToServerEvents[K]>): void {
    this.ensureInitialized();

    // capture outside to avoid generic loss inside the closure
    const _ev = ev;
    const _params = params;

    this.runWhenConnected(() => {
      // this.logger.writeLog(`[IoService] Emitting ${String(_ev)}`, {
      //   severity: LogSeverity.DEBUG,
      // })
      this.socketio!.emit(_ev, ..._params);
    });
  }

  // --------------------
  // Emitters (public API preserved)
  // --------------------
  audioReceived(sessionId: string, audio: Blob): void {
    // Keep legacy laneId='2' default
    this.emit('audioReceived', { laneId: '2', sessionId, audio });
  }

  startSession(sessionId: string, sessionType: SessionType, daypartOverride?: DaypartOverride): void {
    this.connectRetries = 0;
    const sessionConfiguration: SessionConfiguration = { sessionId, sessionType, daypartOverride };

    this.logger.writeLog('[IoService] Webapp is starting a session with Webproxy.', {
      severity: LogSeverity.INFO,
      details: { agentConfig: sessionConfiguration },
    });

    this.emit('startSession', sessionConfiguration);
  }

  /* This emulates the same event and payload generated from NexeoConnectionManager on carArrive from the Nexeo Base Station */

  emulateStartSession(sessionId: string, sessionType: SessionType, daypartOverride?: DaypartOverride): void {
    const sessionConfiguration: SessionConfiguration = { sessionId, sessionType, daypartOverride };
    this.emit('startOrderConversation', sessionConfiguration);
  }

  endSession(sessionId: string): void {
    // Intentionally uses direct .emit to preserve sequencing (end first, then clear listeners)
    this.socketio?.emit('endSession', sessionId);
    this.removeListeners();
    this.logger.writeLog('[IoService] Ended session and removed listeners.', { severity: LogSeverity.INFO });
  }

  resumeSession(sessionId: string): void {
    this.emit('resumeSession', sessionId);
  }

  resumeSessionFromUniqueAudio(sessionId: string, phraseId: string): void {
    this.emit('resumeSessionFromUniqueAudio', sessionId, phraseId);
  }

  sendCustomAudio(sessionId: string, audioId: string): void {
    this.emit('sendCustomAudio', sessionId, audioId);
  }

  // Tell the webproxy this client wants live drive-thru customer audio. The webproxy emits
  // customerAudioReceived only to subscribed sockets, so this must be sent before audio will flow.
  subscribeCustomerPlayback(): void {
    this.logger.writeLog('[IoService] Starting customerPlayback', {
      severity: LogSeverity.DEBUG,
    });
    this.customerPlaybackSubscribed = true;
    this.emit('subscribeCustomerPlayback');
  }

  unsubscribeCustomerPlayback(): void {
    this.customerPlaybackSubscribed = false;
    this.socketio?.emit('unsubscribeCustomerPlayback');
  }

  requestPriceOverlay(request: LtoPricingRequest): void {
    this.logger.writeLog('[IoService] Requesting LTO pricing from WebProxy', {
      severity: LogSeverity.DEBUG,
      requestPayload: request,
    });
    this.emit('pricingRequest', request);
  }

  // --------------------
  // Listeners (public API preserved)
  // --------------------
  uniqueAudioReceived(callback: (transcript: string, sessionId: string, phraseId: string) => void): void {
    this.socketio?.on('uniqueAudioReceived', callback);
  }

  customerAudioReceived(callback: (audio: ArrayBuffer) => void): void {
    this.socketio?.on('customerAudioReceived', callback);
  }

  // TODO: Expand this on/off logic to account for the rest of the events
  // for the webProxy socket so we can handle turning off events for when
  // components are destroyed.
  transcriptionReceived(callback: (transcript: string, sessionId: string) => void, on: boolean = true): void {
    if (on) {
      this.socketio?.on('transcriptReceived', callback);
    } else {
      this.socketio?.off('transcriptReceived', callback);
    }
  }

  onAgentAudioReceived(callback: any): void {
    this.socketio?.on('agentAudioReceived', callback);
  }

  onAgentResponseReceived(callback: any): void {
    this.socketio?.on('agentResponseReceived', callback);
  }

  onAgentIsProcessing(callback: (response: AgentIsProcessingEvent) => void): void {
    this.socketio?.on('agentIsProcessing', callback);
  }

  setAuthenticationErrorListener(callback: () => void): void {
    this.socketio?.on('authenticationError', callback);
  }

  onStop(callback: (sessionId: string) => void): void {
    this.socketio?.on('stop', callback);
  }

  onStart(callback: (sessionEvent: SessionEvent) => void): void {
    this.socketio?.on('start', callback);
  }

  onSessionStarted(callback: (state: SessionStartedState) => void): void {
    this.socketio?.on('sessionStarted', callback);
  }

  onSessionResumed(callback: () => void): void {
    this.socketio?.on('sessionResumed', callback);
  }

  onCartPriced(callback: any): void {
    this.socketio?.on('cartPriced', callback);
  }

  onRecommendationsPriced(callback: any): void {
    this.socketio?.on('recommendationsPriced', callback);
  }

  onCloseLane(callback: (sessionId: string) => void): void {
    this.socketio?.on('closeLane', callback);
  }

  onOpenLane(callback: (sessionId: string) => void): void {
    this.socketio?.on('openLane', callback);
  }

  onStandbyLane(callback: (sessionId: string) => void): void {
    this.socketio?.on('standbyLane', callback);
  }

  onAgentSwitchedLanguage(callback: (response: LanguageChangeEvent) => void): void {
    this.socketio?.on('agentSwitchedLanguage', callback);
  }

  onPriceOverlayReceived(callback: (response: LtoPricingResponse) => void): void {
    // Preserve legacy de-dupe behavior only for pricingResponse:
    this.socketio?.off('pricingResponse');
    this.socketio?.on('pricingResponse', (response: LtoPricingResponse) => {
      this.logger.writeLog('[IoService] Received LTO pricing response from WebProxy', {
        response,
        severity: LogSeverity.DEBUG,
      });
      callback(response);
    });
  }

  catchErrors(callback: (event: string, error: any) => void): void {
    this.socketio?.on('connect_error', (error) => {
      if (this.connectRetries < this.maxConnectRetries) {
        this.connectRetries += 1;
        this.logger.writeLog(`[IoService] Retrying connect_error (${error}). Retry #${this.connectRetries}`, {
          severity: LogSeverity.WARNING,
        });
      } else {
        this.logger.writeLog(`[IoService] Exhausted retries for connect_error (${error}).`, {
          severity: LogSeverity.ERROR,
        });
        callback('connect_error', error);
      }
    });

    this.socketio?.on('disconnect', (reason) => {
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        this.logger.writeLog(`[IoService] Received disconnect '${reason}' which is not auto-retried by SocketIO.`, {
          severity: LogSeverity.WARNING,
        });
        callback('disconnect', reason);
      } else if (this.connectRetries < this.maxConnectRetries) {
        this.connectRetries += 1;
        this.logger.writeLog(`[IoService] Retrying disconnect (${reason}). Retry #${this.connectRetries}`, {
          severity: LogSeverity.WARNING,
        });
      } else {
        this.logger.writeLog(`[IoService] Exhausted retries for disconnect (${reason}).`, {
          severity: LogSeverity.ERROR,
        });
        callback('disconnect', reason);
      }
    });
  }

  // --------------------
  // Tiny helpers
  // --------------------
  private safeGetLocal(key: string): string | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeGetUrlParam(key: string): string | null {
    try {
      if (typeof window === 'undefined') return null;
      return new URLSearchParams(window.location.search).get(key);
    } catch {
      return null;
    }
  }

  private safeSetLocal(key: string, value: string): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }

  /** Precedence: URL param > localStorage */
  private resolveStoreId(): string | undefined {
    const fromUrl = this.safeGetUrlParam('storeId');
    if (fromUrl) {
      this.safeSetLocal('storeId', fromUrl);
      this.logger.setStoreId(fromUrl);
      return fromUrl;
    }
    const fromLocal = this.safeGetLocal('storeId') ?? undefined;
    if (fromLocal) this.logger.setStoreId(fromLocal);
    return fromLocal;
  }

  private async getAccessTokenSafely(): Promise<string | undefined> {
    try {
      // Preserve legacy semantics: await a promise-like "accessToken"
      // If your AuthService exposes an Observable, adapt here.
      const token = await this.authService.accessToken;
      return token ?? undefined;
    } catch (_e) {
      return undefined;
    }
  }
}
